import {Record, Map, fromJS} from 'immutable';
import moment from 'moment-timezone';

import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import * as AbstractBudgetActions from 'web-app/react/abstract-bugdet/actions';
import {BUDGET_TYPE} from 'web-app/react/abstract-bugdet/constants';
import {match, makeReducer} from 'web-app/util/redux';

import {type RefreshRevenueProgressStatus} from './types';
import * as Actions from './actions';
import * as Constants from './constants';
import {isFetchRequest} from './helpers';

interface State {
    targetType: Constants.REVENUE_TARGET_TYPES | null;
    behaviors: BehaviorsCollection;
    year: string;
    firstMonth: string;
    numberOfMonths: string;
    fetching: boolean;
    revenueData: any;
    lastUpdatedAt: string | null;
    csv: string | null;
    filter: Constants.REVENUE_FILTER | null;
    status: RefreshRevenueProgressStatus | null;
    refreshRequestedAt: string | null;
    disabledRefreshButton: boolean;
}

export class RevenueReportState extends Record<State>({
    targetType: null,
    behaviors: new BehaviorsCollection([]),
    year: moment().format('YYYY'),
    firstMonth: Constants.DEFAULT_FIRST_MONTH,
    numberOfMonths: Constants.DEFAULT_NUMBER_OF_MONTHS,
    fetching: false,
    revenueData: null,
    lastUpdatedAt: null,
    csv: null,
    filter: null,
    status: null,
    refreshRequestedAt: null,
    disabledRefreshButton: false,
}) {}

export default makeReducer<RevenueReportState>(
    [
        match(
            Actions.fetch,
            (state, {payload}) =>
                new RevenueReportState({
                    targetType: payload.targetType,
                    revenueData: state.revenueData,
                    fetching: true,
                    year: payload.year,
                    filter: payload.filter,
                    firstMonth: payload.firstMonth,
                    numberOfMonths: payload.numberOfMonths,
                    disabledRefreshButton: state.disabledRefreshButton,
                }),
        ),
        match(Actions.refreshUpdateStarted, state =>
            state.merge({
                disabledRefreshButton: true,
            }),
        ),
        match(Actions.refreshUpdateStopped, state =>
            state.merge({
                disabledRefreshButton: false,
            }),
        ),
        match(Actions.progressUpdate.action, (state, {payload}) =>
            state.merge({
                refreshRequestedAt: payload.result.refreshRequestedAt,
                status: payload.result.status,
            }),
        ),
        match(Actions.fetchSuccess, (state, {payload}) =>
            state.merge({
                fetching: false,
                // fromJS used here due to old behavior on merge, where fromJS was called indirectly
                // if the response is from the POST refresh request then ignore it and keep the previous state
                revenueData: isFetchRequest(payload.response) ? fromJS(payload.response) : state.revenueData,
                lastUpdatedAt: payload.response.lastUpdatedAt ?? payload.response.refreshRequestedAt,
                csv: payload.response.csv,
                behaviors: new BehaviorsCollection(payload.response.behaviors),
                disabledRefreshButton: state.disabledRefreshButton,
            }),
        ),
        match(Actions.fetchFailed, state => state.merge({fetching: false})),
        match(AbstractBudgetActions.updateBudgetSuccess, (state, {payload}) => {
            if (payload.type !== BUDGET_TYPE.REVENUE) {
                return state;
            }
            switch (state.targetType) {
                case Constants.REVENUE_TARGET_TYPES.ORGANIZATON:
                    // When looking at the organization revenue report, and updating budget
                    // we simply refetch the report again instead of mutating local state
                    return state;
                case Constants.REVENUE_TARGET_TYPES.INSTITUTION:
                default:
                    return state.merge({
                        revenueData:
                            payload.year === state.year
                                ? state.revenueData.merge({
                                      totals: state.revenueData.get('totals').reduce(
                                          (reduction, value, key) =>
                                              reduction.set(
                                                  key,
                                                  state.revenueData
                                                      .get('totals')
                                                      .get(key)
                                                      .merge(fromJS({budget: payload.response[key]})),
                                              ),
                                          Map(),
                                      ),
                                  })
                                : state.revenueData,
                    });
            }
        }),
    ],
    new RevenueReportState(),
);
